import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Linkedin, Mail, ArrowRight } from "lucide-react";
import { FOUNDER, SITE, publishedVentures } from "@/data/site";
import { WHY } from "@/data/content";
import { PageHero, SectionHead, CtaBand } from "@/components/Blocks";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants } from "@/components/motion";
import { StatusBadge } from "@/components/VentureBits";
import { FounderIntro } from "@/components/home/Stories";
import { useSeo } from "@/lib/seo";

const PRINCIPLES = [
  ["Understand before building", "The most expensive code is the code that solves the wrong problem."],
  ["Honest trade-offs", "We explain the cost of every shortcut and every extra feature."],
  ["Own the outcome", "We care whether the product works for the business, not just whether tickets are closed."],
  ["Earn complexity", "Start simple. Add sophistication when the product has earned it."],
];

export function About() {
  useSeo("About", "SparkIdea Interactive is an independent venture studio and product engineering partner founded by Baltej Singh.");
  return (
    <>
      <PageHero testId="about-hero" eyebrow="About" title={["We believe great ideas", "deserve great execution."]}
        intro="SparkIdea Interactive builds independent software ventures and helps founders turn ambitious ideas into real products." />
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-2">
          <Reveal className="panel p-8 sm:p-10"><p className="eyebrow">Mission</p><p className="mt-5 text-2xl font-semibold leading-snug sm:text-3xl">To turn ambitious ideas into real products and scalable businesses — our own, and those of the founders we work with.</p></Reveal>
          <Reveal delay={0.1} className="panel p-8 sm:p-10"><p className="eyebrow">Why we exist</p><p className="mt-5 text-lg leading-relaxed text-si-muted">Too many good ideas stall between a prototype and a real product — lost in unclear scope, fragile engineering, or a lack of technical leadership. We exist to close that gap with product thinking and hands-on engineering, and to prove our approach by building ventures of our own.</p></Reveal>
        </div>
      </section>
      <FounderIntro />
      <section className="border-t border-si-line bg-si-bg2 py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="Operating principles" title="How we make decisions." />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map(([t, d], i) => (
              <motion.div key={t} variants={itemVariants} className="panel p-7"><span className="font-mono text-xs text-si-cyan">0{i + 1}</span><h3 className="mt-6 text-xl font-bold">{t}</h3><p className="mt-3 text-base text-si-muted">{d}</p></motion.div>
            ))}
          </Stagger>
          <div className="mt-20"><SectionHead eyebrow="Product-building philosophy" title="What guides our work." /></div>
          <div className="mt-10 grid gap-x-12 gap-y-8 sm:grid-cols-2">
            {WHY.map(([t, d]) => <Reveal key={t}><h3 className="text-xl font-bold">{t}</h3><p className="mt-2 text-base text-si-muted">{d}</p></Reveal>)}
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="Venture portfolio" title="What we're building." />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {publishedVentures().map((v) => (
              <Link key={v.slug} to={`/ventures/${v.slug}`} className="group panel p-6 transition-colors hover:border-si-cyan/50" data-testid={`about-venture-${v.slug}`}>
                <StatusBadge status={v.status} />
                <p className="mt-6 text-2xl font-bold">{v.name}</p>
                <p className="mt-1 text-sm text-si-muted">{v.category}</p>
                <ArrowRight className="mt-6 h-5 w-5 text-si-cyan transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBand testId="about-cta" />
    </>
  );
}

export function Founder() {
  useSeo("Baltej Singh — Founder", "Baltej Singh is a technology entrepreneur and software engineer with 16+ years of experience and the founder of SparkIdea Interactive.");
  return (
    <>
      <PageHero testId="founder-hero" eyebrow="Founder" title={FOUNDER.name} intro="Technology entrepreneur, software engineer, and multi-time founder.">
        <Cta href={FOUNDER.linkedin} external size="lg" testId="founder-page-linkedin">Connect on LinkedIn</Cta>
        <Cta href={`mailto:${SITE.email}`} variant="secondary" size="lg" testId="founder-page-email">Email</Cta>
      </PageHero>
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="panel p-6">
              <p className="eyebrow !text-si-muted">At a glance</p>
              <ul className="mt-5 space-y-4 text-base">
                <li><span className="block text-3xl font-extrabold text-cool">16+ years</span><span className="text-si-muted">building software</span></li>
                <li><span className="block text-lg font-bold">Multi-time founder</span><span className="text-si-muted">SaaS & digital products</span></li>
                <li><span className="block text-lg font-bold">Enterprise-grade engineering</span><span className="text-si-muted">applications & platforms</span></li>
              </ul>
              <div className="mt-6 flex gap-3 border-t border-si-line pt-5">
                <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="grid h-10 w-10 place-items-center rounded-lg border border-si-line hover:border-si-cyan"><Linkedin className="h-4 w-4" /></a>
                <a href={`mailto:${SITE.email}`} aria-label="Email" className="grid h-10 w-10 place-items-center rounded-lg border border-si-line hover:border-si-cyan"><Mail className="h-4 w-4" /></a>
              </div>
            </div>
          </div>
          <div className="space-y-6 text-lg leading-relaxed text-si-muted lg:col-span-8">
            <Reveal><p className="text-2xl font-semibold leading-snug text-si-text">{FOUNDER.bio}</p></Reveal>
            <Reveal><h2 className="mt-10 text-2xl font-bold text-si-text">Product-building philosophy</h2><p className="mt-3">Combine hands-on engineering with product strategy. Understand the problem before writing code, make technical trade-offs explicit, and build systems that can grow as the business does.</p></Reveal>
            <Reveal><h2 className="mt-10 text-2xl font-bold text-si-text">Why SparkIdea Interactive</h2><p className="mt-3">Through SparkIdea Interactive, Baltej is building independent software ventures — including Nautis, DemoVexa, and Qikod — while helping other founders bring their products to life.</p></Reveal>
          </div>
        </div>
      </section>
      <CtaBand testId="founder-cta-band" title="Want to talk through your idea?" primary="Start a Conversation" />
    </>
  );
}
